import React, { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import type { QuoteFile } from 'paintscout'
import { Button, IconButton, makeStyles, Spinner, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'

// __PDF_WORKER_URL__ is injected by Vite's `define` (apps/app/vite.config.ts) so the
// worker is served from our own origin rather than an external CDN.
// Falls back to unpkg if the variable wasn't injected (e.g. in tests or other apps).
declare const __PDF_WORKER_URL__: string
pdfjs.GlobalWorkerOptions.workerSrc =
  typeof __PDF_WORKER_URL__ !== 'undefined' && __PDF_WORKER_URL__
    ? __PDF_WORKER_URL__
    : `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: theme.spacing(1)
    },
    documentWrapper: {
      width: '100%',
      height: '60vh',
      maxHeight: 700,
      overflow: 'auto',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        height: '50vh'
      }
    },
    nav: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1)
    },
    openButton: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    errorMessage: {
      padding: theme.spacing(2)
    }
  }),
  { name: 'PdfViewer' }
)

interface PdfViewerProps {
  file: QuoteFile
}

export default function PdfViewer({ file }: PdfViewerProps) {
  const classes = useStyles()
  const [numPages, setNumPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = containerRef.current
    if (!node) return
    setContainerWidth(node.clientWidth)
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  function handleOpen() {
    if (file.src && file.src.startsWith('https://')) {
      window.open(file.src, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.documentWrapper} ref={containerRef}>
        <Document
          file={file.src}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          loading={<Spinner />}
          error={
            <Typography variant="body2" color="error" align="center" className={classes.errorMessage}>
              Failed to load PDF.{' '}
              {file.src?.startsWith('https://') && (
                <a href={file.src} target="_blank" rel="noopener noreferrer">
                  Open in browser
                </a>
              )}
            </Typography>
          }
        >
          <Page
            pageNumber={currentPage}
            width={containerWidth ?? undefined}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>

      {numPages > 1 && (
        <div className={classes.nav}>
          <IconButton size="small" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
            <NavigateBeforeIcon />
          </IconButton>
          <Typography variant="body2">
            {currentPage} / {numPages}
          </Typography>
          <IconButton size="small" disabled={currentPage >= numPages} onClick={() => setCurrentPage((p) => p + 1)}>
            <NavigateNextIcon />
          </IconButton>
        </div>
      )}

      {file.src?.startsWith('https://') && (
        <div className={classes.openButton}>
          <Button variant="text" startIcon={<OpenInNewIcon fontSize="small" />} onClick={handleOpen}>
            Open PDF
          </Button>
        </div>
      )}
    </div>
  )
}
